/* src/components/pages/Subircarpetacli.jsx - Modificado el 26-01-2026 */
/* ------- subir carpeta tributaria para clientes y enrolados -------- */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TextField, Box, Button, Stack, Typography,
  MenuItem } from '@mui/material';

import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { DataGrid } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';
import { CustomToolbar } from "../../components/datagrid/CustomToolbar";

import { RiFolderCheckFill, RiUpload2Line, RiReplyAllFill } from "react-icons/ri";

import { useAuth } from "../../context/AuthContext"; // token

import { useFeedback } from '../../context/FeedbackContext'; // Mensajes breves

/* Validación del Rut (Módulo 11) */
const validarRut = (rut) => {
  let valor = rut.replace(/\./g, '').replace('-', '');
  let cuerpo = valor.slice(0, -1);
  let dv = valor.slice(-1).toUpperCase();
  let suma = 0;
  let multiplo = 2;

  for (let i = 1; i <= cuerpo.length; i++) {
    let index = multiplo * valor.charAt(cuerpo.length - i);
    suma += index;
    multiplo = multiplo < 7 ? multiplo + 1 : 2;
  }

  let dvEsperado = 11 - (suma % 11);
  dvEsperado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
  return dv === dvEsperado;
};

const Subircarpetacli = ({ modo }) => {
  const { user } = useAuth();

  /* obtener rut del usuario */
  const rut = user?.usuario;

  const [bitacora, setBitacora] = useState([]);
  const [loading, setLoading] = useState(false);

  const inputRef = useRef(null);   // 👉 referencia al input file

  const [file, setFile] = useState(null); // 👈 archivo PDF seleccionado
  const [tipo, setTipo] = useState('creditos');

  /* Estados para paginación desde el servidor */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  /* ✅ rut válido derivado */
  const rutValido = React.useMemo(() => {
    if (!rut) return true;
    return validarRut(rut);
  }, [rut]);

  /* Dialogo y confirmación */
  const { showFeedback } = useFeedback(); // Mensajes breves no bloqueantes
  
  /* Función para boton cancelar */
  const handleCancel = () => {
    setFile(null);
    if (inputRef.current) {
      inputRef.current.value = ""; // 👉 resetea el input file
    }
  };

  /* Manejo del cambio de carpetas */
  const handleTipoChange = (event) => {
    setTipo(event.target.value);
  };

  /* cargar bitacora de scraping libro ventas */
  const fetchBitacora = useCallback(async () => {
    if (!rutValido || !rut) {
      showFeedback({
        message: 'El Rut ingresado no es válido',
        severity: 'error',
      });
      setBitacora([]);
      setRowCount(0);
      return;
    }

    setLoading(true);

    try {
      const RutCli = rut
        .replace(/\./g, '')
        .replace('-', '')
        .padStart(10, '0')
        .toUpperCase();

      const { data } = await api.post('/subircarpeta/getbitacora', {
        RutCli,          // rut cliente
        RutEnr: '',      // rut del enrolado
        TipCli: 1,        // 1=cliente 2=enrolado
        page,
        pageSize,
      });

      if (data?.success) {
        setBitacora(data.rows || []);
        setRowCount(data.total || 0);
      } else {
        setBitacora([]);
        setRowCount(0);
      }
    } catch {
      setBitacora([]);
      setRowCount(0);
      showFeedback({
        message: 'No fue posible obtener la bitácora',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [rut, rutValido, page, pageSize, showFeedback]);

  /* llamar a cargar bitacora */
  useEffect(() => {
    fetchBitacora();
  }, [fetchBitacora]);

  const dataGridSlots = React.useMemo(() => ({
    toolbar: CustomToolbar,
  }), []);

  const dataGridSlotProps = React.useMemo(() => ({
    toolbar: {
      showQuickFilter: true,
      quickFilterProps: { debounceMs: 400 },
    },
  }), []);

  const paginationModel = React.useMemo(
    () => ({ page, pageSize }),
    [page, pageSize]
  );

  const localeText = React.useMemo(
    () => esES.components.MuiDataGrid.defaultProps.localeText,
    []
  );

  const getRowId = React.useCallback((row) => row.id, []);


  /* Configuración de MUI DataGrid */
  const columns = React.useMemo(() => ([
    {
      field: 'id',
      headerName: 'Id Registro',
      width: 120,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "fecha",
      headerName: "Fecha",
      width: 200,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        if (!params.value) return "—";
        return new Date(params.value).toLocaleString("es-CL");
      },
    },
    {
      field: 'tipo',
      headerName: "Tipo",
      width: 100,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'rutcli',
      headerName: "Rut Cliente",
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'rutenr',
      headerName: "Rut Enrolado",
      width: 130,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'detalle',
      headerName: "Detalle",
      width: 280,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'nombre',
      headerName: 'Razón Social',
      flex: 1,                 // 👈 ocupa todo el espacio disponible
      minWidth: 280,           // 👈 evita que se colapse
      align: 'left',
      headerAlign: 'center',
    },
  ]), []);

  /* Subir PDF de carpeta tributaria */
  const handleUpload = async () => {
    if (!file) {
      showFeedback({
        message: 'Debe selecciona un archivo PDF',
        severity: 'error',
      });
      return;
    }

    try {
      const tipoCarpeta = tipo === "creditos" ? 1 : 2;

      const formData = new FormData();
      formData.append("file", file);
      formData.append("RutCli", rut);
      formData.append("RutEnr", '');
      formData.append("NomCli", user?.nombre);
      formData.append("TipCli", tipoCarpeta); // 1=créditos 2=personalizada //

      const response = await api.post('/subircarpeta/uploadcarpeta', formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      if (response.data.success) {
        showFeedback({
          message: 'La carpeta tributaria fue subida correctamente.',
          severity: 'info',
        });
        setFile(null);
        if (inputRef.current) {
          inputRef.current.value = ""; // 👉 limpia input después de subir
        }
        fetchBitacora(); // refrescar tabla

      } else {
        showFeedback({
          message: 'No fue posible subir el archivo.',
          severity: 'error',
        });
      }

    } catch (error) {
      showFeedback({
        message: 'No fue posible subir el archivo, intente más tarde.',
        severity: 'error',
      });
    }
  };

  /* Render */
  return (
    <Box sx={{ width: '100%', pt: 0, overflow: 'visible' }}>
      {/* Barra de Controles */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <TextField
          id="tipo-input"
          select
          label="Tipo"
          value={tipo}
          onChange={handleTipoChange}
          variant="outlined"
          required
          sx={{
            width: 150,
            height: '40px',
            '& .MuiOutlinedInput-root': { height: '40px' },
            '& .MuiSelect-select': {
              textAlign: 'center',
            },
          }}
        >
          <MenuItem value="creditos">Créditos</MenuItem>
          <MenuItem value="personalizada">Personalizada</MenuItem>
        </TextField>

        {/* Subir PDF */}
        <input
          accept="application/pdf"
          style={{ display: 'none' }}
          id="upload-pdf"
          type="file"
          ref={inputRef} // 👉 referencia al input
          onChange={(e) => setFile(e.target.files[0] || null)}
        />
        <label htmlFor="upload-pdf">
          <Button
            variant="contained"
            component="span"
            sx={{
              width: '145px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#4caf50',
              color: 'white',
              '&:hover': { backgroundColor: '#388e3c', color: 'white' },
            }}>
            <RiFolderCheckFill style={{ fontSize: '1.5rem', marginRight: '8px' }} />
            Buscar PDF
          </Button>
        </label>

        {/* Botón subir */}
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!file}
          sx={{
            width: '145px',
            height: '40px',
            textTransform: 'none',
            backgroundColor: '#8b888e',
            color: 'white',
            '&:hover': { backgroundColor: '#9759c7', color: 'white' },
          }}>
          <RiUpload2Line style={{ fontSize: '1.5rem', marginRight: '8px' }} />
          Subir PDF
        </Button>

        {/* Botón Cancelar */}
        <Button
          variant="contained"
          onClick={handleCancel}
          sx={{
            width: '145px',
            height: '40px',
            textTransform: 'none',
            backgroundColor: '#8b888e', // Color de fondo para cancelar
            color: 'white',
            '&:hover': {
              backgroundColor: '#b9770e', // Color al pasar el cursor //
              color: 'white',
            },
            '& .MuiButton-startIcon': {
              color: 'white',
            },
          }}
        >
          {/* Icono de cancelar */}
          <RiReplyAllFill className="m-2" style={{ fontSize: '1.5rem' }} />
          Cancelar
        </Button>

        {/* mostrar nombre del archivo */}
        <TextField
          value={file ? file.name : ""}
          label="Archivo seleccionado..."
          variant="outlined"
          disabled
          sx={{
            mt: 0,
            width: '765px',
            height: '40px',
            '& .MuiOutlinedInput-root': {
              height: '40px',
              '& input': {
                color: '#ca531f',
                WebkitTextFillColor: '#ca531f',
              },
            },
            '& .MuiInputLabel-root': {
              color: '#ca531f',
            },
          }}
        />
      </Stack>

      {/* Configuración MIU DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 210px)', // ajusta según tu layout
          minHeight: 0,
        }}
      >
        <DataGrid
          slots={dataGridSlots}
          slotProps={dataGridSlotProps}
          rows={bitacora}
          columns={columns}
          getRowId={getRowId}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={paginationModel}

          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;
            setPage(isPageSizeChange ? 0 : newPage);
            setPageSize(newPageSize);
          }}
          localeText={localeText}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        {rowCount.toLocaleString('es-CL')} registros encontrados
      </Typography>

    </Box>
  );
};

export default Subircarpetacli;
